
import { useState, useRef } from 'react';
import { CarouselItem } from "@/components/ui/carousel";
import { transformDetailImage } from "@/utils/cloudinary";

interface ProductImageSlideProps {
  url: string;
  index: number;
  productName: string;
  selectedColor?: any;
  aspectRatio?: 'square' | 'portrait' | 'landscape' | 'auto';
  objectFit?: 'cover' | 'contain';
  imageTransitioning: boolean;
}

export const ProductImageSlide = ({ 
  url, 
  index, 
  productName, 
  selectedColor, 
  aspectRatio = 'auto',
  objectFit = 'cover',
  imageTransitioning 
}: ProductImageSlideProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [useCloudinary, setUseCloudinary] = useState(true);
  const prevUrlRef = useRef(url);

  // Reset state only when URL actually changes
  if (prevUrlRef.current !== url) {
    prevUrlRef.current = url;
    setImageLoaded(false);
    setUseCloudinary(true);
  }

  const displayUrl = useCloudinary ? transformDetailImage(url) : url;
  const isLoading = !imageLoaded || imageTransitioning;

  return (
    <CarouselItem key={`${selectedColor?.id || 'all'}-${url}-${index}`}>
      <div 
        className="product-image-viewport relative mx-auto w-full max-w-[752px] md:w-[752px] bg-background rounded-lg"
      >
        {/* Loading placeholder */}
        {isLoading && (
          <div className="absolute inset-0 animate-pulse flex items-center justify-center z-10">
            <div className="w-8 h-8 border-2 border-muted-foreground/40 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <img 
          src={displayUrl || '/placeholder.svg'} 
          alt={`${productName} - image ${index + 1}`}
          className={`h-full w-auto block mx-auto object-contain object-bottom transition-opacity duration-300 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          onError={(e) => {
            if (useCloudinary) {
              console.warn('Cloudinary transformation failed, falling back to original:', url);
              setUseCloudinary(false);
            } else {
              console.error('Image failed to load:', url);
              e.currentTarget.src = '/placeholder.svg';
            }
            setImageLoaded(true);
          }}
          onLoad={() => setImageLoaded(true)}
          loading={index === 0 ? 'eager' : 'lazy'}
        />
      </div>
    </CarouselItem>
  );
};
